import {StateCreator} from "zustand/vanilla";
import {Activity, ActivityFilters} from "@/pb/api/devplan/types/activity";
import {FetchActivityRequest, GetProjectActivityResponse, RestoreActivityRequest, RestoreActivityResponse} from "@/pb/api/devplan/services/web/company";
import {projectActivityFetchAPI, projectActivityRestoreAPI} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";

export interface ActivityStoreState {
  projectActivities: Record<string, Activity[]>;
  projectActivityCursors: Record<string, string | undefined>;
  restoring: boolean;

  fetchProjectActivity(companyId: number, projectId: string, request: FetchActivityRequest): Promise<void>;
  fetchMoreProjectActivity(companyId: number, projectId: string, request: FetchActivityRequest): Promise<void>;
  clearProjectActivity(projectId: string): void;
  restoreActivity(companyId: number, projectId: string, activityIds: string[]): Promise<number>;
}

export const createActivitySlice: StateCreator<
  ActivityStoreState,
  [],
  [],
  ActivityStoreState
> = (set, get) => ({
  projectActivities: {},
  projectActivityCursors: {},
  restoring: false,

  fetchProjectActivity: async (companyId, projectId, request) => {
    const resp = await fetch(projectActivityFetchAPI(companyId, projectId), {
      method: "POST",
      body: JSON.stringify(FetchActivityRequest.toJSON(request)),
      headers: {"Content-Type": "application/json"},
    });
    const js = await jr(resp);
    const {activities, nextCursor} = GetProjectActivityResponse.fromJSON(js);
    set(s => ({
      ...s,
      projectActivities: {...s.projectActivities, [projectId]: activities},
      projectActivityCursors: {...s.projectActivityCursors, [projectId]: nextCursor ?? undefined},
    }));
  },

  fetchMoreProjectActivity: async (companyId, projectId, request) => {
    const cursor = get().projectActivityCursors[projectId];
    if (!cursor) return;

    // copy the request upserting the cursor
    const reqWithCursor: FetchActivityRequest = {
      ...request,
    };
    reqWithCursor.filters = reqWithCursor.filters || ActivityFilters.create()
    reqWithCursor.filters.cursor = cursor

    const resp = await fetch(projectActivityFetchAPI(companyId, projectId), {
      method: "POST",
      body: JSON.stringify(FetchActivityRequest.toJSON(reqWithCursor)),
      headers: {"Content-Type": "application/json"},
    });
    const js = await jr(resp);
    const {activities: newActivities, nextCursor} = GetProjectActivityResponse.fromJSON(js);
    set(s => ({
      ...s,
      projectActivities: {
        ...s.projectActivities,
        [projectId]: [...(s.projectActivities[projectId] ?? []), ...newActivities],
      },
      projectActivityCursors: {...s.projectActivityCursors, [projectId]: nextCursor ?? undefined},
    }));
  },

  clearProjectActivity: (projectId) => {
    set(s => {
      const {[projectId]: _a, ...restActivities} = s.projectActivities;
      const {[projectId]: _c, ...restCursors} = s.projectActivityCursors;
      return {...s, projectActivities: restActivities, projectActivityCursors: restCursors};
    });
  },

  restoreActivity: async (companyId, projectId, activityIds) => {
    set(s => ({...s, restoring: true}));
    try {
      const resp = await fetch(projectActivityRestoreAPI(companyId, projectId), {
        method: "POST",
        body: JSON.stringify(RestoreActivityRequest.toJSON({lookup: {$case: "byIds", value: {ids: activityIds}}})),
        headers: {"Content-Type": "application/json"},
      });
      const js = await jr(resp);
      const {restoredCount} = RestoreActivityResponse.fromJSON(js);
      return restoredCount;
    } finally {
      set(s => ({...s, restoring: false}));
    }
  },
});
